import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { 
  TrendingUp, Brain, Zap, Network, Circle,
  ArrowUpRight, ArrowDownRight, Minus, RefreshCw,
  Target, Award, BookOpen, ChevronRight
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function CognitiveGrowthGraph({ courseId }) {
  const [cognitiveData, setCognitiveData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedNode, setSelectedNode] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    console.log('CognitiveGrowthGraph: Component mounted, loading data...');
    loadCognitiveData();
  }, [courseId]);

  const loadCognitiveData = async () => {
    const token = localStorage.getItem('faheem_token');
    console.log('CognitiveGrowthGraph: Token exists:', !!token);
    
    if (!token) {
      console.log('CognitiveGrowthGraph: No token, setting loading to false');
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      const url = courseId 
        ? `${BACKEND_URL}/api/formative-assessment/analyze?access_token=${token}&course_id=${courseId}`
        : `${BACKEND_URL}/api/formative-assessment/analyze?access_token=${token}`;
      
      console.log('CognitiveGrowthGraph: Fetching from:', url);
      const response = await axios.get(url);
      console.log('CognitiveGrowthGraph: Response received:', response.data);
      
      if (response.data && response.data.cognitive_data) {
        setCognitiveData(response.data.cognitive_data);
      }
    } catch (err) {
      console.error('CognitiveGrowthGraph: Failed to load cognitive data:', err);
      setError(err.message);
    } finally {
      console.log('CognitiveGrowthGraph: Setting loading to false');
      setLoading(false);
    }
  };

  const getGrowthIcon = (growth) => {
    if (growth > 0) return <ArrowUpRight className="w-3 h-3" />;
    if (growth < 0) return <ArrowDownRight className="w-3 h-3" />;
    return <Minus className="w-3 h-3" />;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'mastered': return { bg: '#22c55e', light: '#dcfce7', text: '#166534' };
      case 'progressing': return { bg: '#eab308', light: '#fef9c3', text: '#854d0e' };
      case 'needs_work': return { bg: '#ef4444', light: '#fee2e2', text: '#991b1b' };
      default: return { bg: '#94a3b8', light: '#f1f5f9', text: '#475569' };
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'mastered': return 'Mastered';
      case 'progressing': return 'Progressing';
      case 'needs_work': return 'Needs Work';
      default: return 'Not Started';
    }
  };

  if (loading) {
    return (
      <Card className="border-slate-200" data-testid="cognitive-growth-loading">
        <CardContent className="py-12">
          <div className="flex flex-col items-center justify-center gap-3">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600"></div>
            <p className="text-sm text-slate-500">Loading cognitive data...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (cognitiveData.length === 0) {
    return (
      <Card className="border-slate-200" data-testid="cognitive-growth-empty">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Network className="w-5 h-5 text-purple-600" />
            Cognitive Growth Graph
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-10" data-testid="cognitive-growth-empty-state">
            <div className="w-20 h-20 bg-purple-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <Brain className="w-10 h-10 text-purple-300" />
            </div>
            <p className="text-slate-600 font-medium">No cognitive data yet</p>
            <p className="text-sm text-slate-400 mt-1">Complete quizzes to see your learning network</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Calculate stats
  const overallScore = Math.round(cognitiveData.reduce((sum, n) => sum + n.score, 0) / cognitiveData.length);
  const masteredCount = cognitiveData.filter(n => n.status === 'mastered').length;
  const progressingCount = cognitiveData.filter(n => n.status === 'progressing').length;
  const needsWorkCount = cognitiveData.filter(n => n.status === 'needs_work').length;

  // SVG dimensions
  const svgWidth = 500;
  const svgHeight = 400;
  const centerX = svgWidth / 2;
  const centerY = svgHeight / 2;
  const radius = 140;

  // Calculate node positions in a circle
  const nodes = cognitiveData.map((data, index) => {
    const angle = (index / cognitiveData.length) * 2 * Math.PI - Math.PI / 2;
    const nodeRadius = Math.max(28, Math.min(40, 25 + data.attempts * 3));
    return {
      ...data,
      x: centerX + radius * Math.cos(angle),
      y: centerY + radius * Math.sin(angle),
      radius: nodeRadius,
      angle
    };
  });

  return (
    <Card className="border-slate-200" data-testid="cognitive-growth-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg" data-testid="cognitive-growth-title">
            <Network className="w-5 h-5 text-purple-600" />
            Cognitive Growth Graph
          </CardTitle>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={loadCognitiveData}
            className="text-slate-500 hover:text-purple-600"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        
        {/* Summary Stats */}
        <div className="grid grid-cols-4 gap-3">
          <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4 text-center border border-purple-200">
            <Brain className="w-6 h-6 text-purple-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-purple-700">{overallScore}%</p>
            <p className="text-xs text-purple-600 font-medium">Overall</p>
          </div>
          <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4 text-center border border-green-200">
            <Award className="w-6 h-6 text-green-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-green-700">{masteredCount}</p>
            <p className="text-xs text-green-600 font-medium">Mastered</p>
          </div>
          <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-xl p-4 text-center border border-yellow-200">
            <TrendingUp className="w-6 h-6 text-yellow-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-yellow-700">{progressingCount}</p>
            <p className="text-xs text-yellow-600 font-medium">Progressing</p>
          </div>
          <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl p-4 text-center border border-red-200">
            <Target className="w-6 h-6 text-red-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-red-700">{needsWorkCount}</p>
            <p className="text-xs text-red-600 font-medium">Needs Work</p>
          </div>
        </div>

        {/* SVG Network Graph */}
        <div className="relative bg-gradient-to-br from-slate-50 via-purple-50/30 to-blue-50/30 rounded-2xl p-4 border border-slate-200 overflow-hidden">
          <svg 
            viewBox={`0 0 ${svgWidth} ${svgHeight}`}
            className="w-full h-auto"
            style={{ maxHeight: '400px', minHeight: '300px' }}
          >
            {/* Background grid pattern */}
            <defs>
              <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
                <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#e2e8f0" strokeWidth="0.5" opacity="0.5"/>
              </pattern>
              
              {/* Gradient for center node */}
              <radialGradient id="centerGradient" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#8b5cf6" />
                <stop offset="100%" stopColor="#6d28d9" />
              </radialGradient>
              
              {/* Glow filter */}
              <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                <feMerge>
                  <feMergeNode in="coloredBlur"/>
                  <feMergeNode in="SourceGraphic"/>
                </feMerge>
              </filter>

              {/* Shadow filter */}
              <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
                <feDropShadow dx="0" dy="2" stdDeviation="3" floodOpacity="0.15"/>
              </filter>
            </defs>

            {/* Background */}
            <rect width={svgWidth} height={svgHeight} fill="url(#grid)" />

            {/* Circular orbit lines */}
            <circle 
              cx={centerX} 
              cy={centerY} 
              r={radius} 
              fill="none" 
              stroke="#cbd5e1" 
              strokeWidth="1" 
              strokeDasharray="8,4"
              opacity="0.6"
            />
            <circle 
              cx={centerX} 
              cy={centerY} 
              r={radius - 30} 
              fill="none" 
              stroke="#e2e8f0" 
              strokeWidth="1" 
              strokeDasharray="4,4"
              opacity="0.4"
            />

            {/* Connection lines from center to nodes */}
            {nodes.map((node, i) => (
              <line
                key={`line-${i}`}
                x1={centerX}
                y1={centerY}
                x2={node.x}
                y2={node.y}
                stroke={node.color}
                strokeWidth="2"
                strokeOpacity="0.3"
                strokeLinecap="round"
              />
            ))}

            {/* Connection lines between adjacent nodes */}
            {nodes.map((node, i) => {
              const nextNode = nodes[(i + 1) % nodes.length];
              return (
                <line
                  key={`arc-${i}`}
                  x1={node.x}
                  y1={node.y}
                  x2={nextNode.x}
                  y2={nextNode.y}
                  stroke="#94a3b8"
                  strokeWidth="1"
                  strokeOpacity="0.2"
                  strokeDasharray="4,4"
                />
              );
            })}

            {/* Center node */}
            <g filter="url(#shadow)">
              <circle
                cx={centerX}
                cy={centerY}
                r={50}
                fill="url(#centerGradient)"
              />
              <circle
                cx={centerX}
                cy={centerY}
                r={50}
                fill="none"
                stroke="white"
                strokeWidth="3"
                opacity="0.3"
              />
            </g>
            
            {/* Center node content */}
            <text
              x={centerX}
              y={centerY - 8}
              textAnchor="middle"
              fill="white"
              fontSize="20"
              fontWeight="bold"
              fontFamily="system-ui, -apple-system, sans-serif"
            >
              {overallScore}%
            </text>
            <text
              x={centerX}
              y={centerY + 12}
              textAnchor="middle"
              fill="white"
              fontSize="11"
              opacity="0.9"
              fontFamily="system-ui, -apple-system, sans-serif"
            >
              Overall Score
            </text>

            {/* Topic nodes */}
            {nodes.map((node, i) => {
              const colors = getStatusColor(node.status);
              const isSelected = selectedNode === i;
              
              return (
                <g 
                  key={`node-${i}`} 
                  onClick={() => setSelectedNode(isSelected ? null : i)}
                  style={{ cursor: 'pointer' }}
                  filter={isSelected ? "url(#glow)" : "url(#shadow)"}
                >
                  {/* Node circle */}
                  <circle
                    cx={node.x}
                    cy={node.y}
                    r={node.radius + (isSelected ? 4 : 0)}
                    fill={colors.bg}
                    stroke="white"
                    strokeWidth={isSelected ? 4 : 3}
                  />
                  
                  {/* Score text */}
                  <text
                    x={node.x}
                    y={node.y + 1}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fill="white"
                    fontSize="13"
                    fontWeight="bold"
                    fontFamily="system-ui, -apple-system, sans-serif"
                  >
                    {node.score}%
                  </text>

                  {/* Topic name - positioned based on angle */}
                  <text
                    x={node.x + (Math.cos(node.angle) * (node.radius + 18))}
                    y={node.y + (Math.sin(node.angle) * (node.radius + 18))}
                    textAnchor={node.angle > Math.PI / 2 && node.angle < 3 * Math.PI / 2 ? "end" : "start"}
                    dominantBaseline="middle"
                    fill="#475569"
                    fontSize="11"
                    fontWeight="500"
                    fontFamily="system-ui, -apple-system, sans-serif"
                  >
                    {node.name.length > 18 ? node.name.substring(0, 18) + '...' : node.name}
                  </text>

                  {/* Growth indicator badge */}
                  {node.growth !== 0 && (
                    <g>
                      <circle
                        cx={node.x + node.radius - 5}
                        cy={node.y - node.radius + 5}
                        r={10}
                        fill={node.growth > 0 ? '#22c55e' : '#ef4444'}
                        stroke="white"
                        strokeWidth="2"
                      />
                      <text
                        x={node.x + node.radius - 5}
                        y={node.y - node.radius + 6}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        fill="white"
                        fontSize="10"
                        fontWeight="bold"
                      >
                        {node.growth > 0 ? '↑' : '↓'}
                      </text>
                    </g>
                  )}
                </g>
              );
            })}
          </svg>

          {/* Legend */}
          <div className="absolute bottom-4 left-4 bg-white/90 backdrop-blur-sm rounded-lg px-3 py-2 shadow-sm border border-slate-200">
            <div className="flex gap-4 text-xs">
              <span className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded-full bg-green-500"></span>
                <span className="text-slate-600">Mastered</span>
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded-full bg-yellow-500"></span>
                <span className="text-slate-600">Progressing</span>
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded-full bg-red-500"></span>
                <span className="text-slate-600">Needs Work</span>
              </span>
            </div>
          </div>
        </div>

        {/* Selected Node Details */}
        {selectedNode !== null && nodes[selectedNode] && (
          <div 
            className={`rounded-xl p-5 border-2 transition-all duration-300`}
            style={{ 
              backgroundColor: getStatusColor(nodes[selectedNode].status).light,
              borderColor: getStatusColor(nodes[selectedNode].status).bg 
            }}
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <h4 className="font-semibold text-lg text-slate-900">{nodes[selectedNode].name}</h4>
                <Badge 
                  className="mt-1"
                  style={{ 
                    backgroundColor: getStatusColor(nodes[selectedNode].status).bg,
                    color: 'white'
                  }}
                >
                  {getStatusLabel(nodes[selectedNode].status)}
                </Badge>
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold" style={{ color: getStatusColor(nodes[selectedNode].status).bg }}>
                  {nodes[selectedNode].score}%
                </p>
                <p className="text-xs text-slate-500">Current Score</p>
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-white/60 rounded-lg p-3 text-center">
                <p className="text-xl font-bold text-slate-800">{nodes[selectedNode].best_score}%</p>
                <p className="text-xs text-slate-500">Best Score</p>
              </div>
              <div className="bg-white/60 rounded-lg p-3 text-center">
                <p className="text-xl font-bold text-slate-800">{nodes[selectedNode].attempts}</p>
                <p className="text-xs text-slate-500">Attempts</p>
              </div>
              <div className="bg-white/60 rounded-lg p-3 text-center">
                <p className={`text-xl font-bold flex items-center justify-center gap-1 ${
                  nodes[selectedNode].growth >= 0 ? 'text-green-600' : 'text-red-600'
                }`}>
                  {getGrowthIcon(nodes[selectedNode].growth)}
                  {nodes[selectedNode].growth >= 0 ? '+' : ''}{nodes[selectedNode].growth}%
                </p>
                <p className="text-xs text-slate-500">Growth</p>
              </div>
            </div>
          </div>
        )}

        {/* Topic List */}
        <div>
          <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
            <BookOpen className="w-4 h-4 text-slate-600" />
            All Topics ({cognitiveData.length})
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {cognitiveData.map((node, idx) => {
              const colors = getStatusColor(node.status);
              const isSelected = selectedNode === idx;
              
              return (
                <button
                  key={idx}
                  onClick={() => setSelectedNode(isSelected ? null : idx)}
                  className={`flex items-center justify-between p-3 rounded-lg border transition-all text-left ${
                    isSelected 
                      ? 'ring-2 ring-purple-400 border-purple-300 bg-purple-50' 
                      : 'border-slate-200 bg-white hover:bg-slate-50 hover:border-slate-300'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold"
                      style={{ backgroundColor: colors.bg }}
                    >
                      {node.score}%
                    </div>
                    <div>
                      <p className="font-medium text-slate-900 text-sm">
                        {node.name.length > 25 ? node.name.substring(0, 25) + '...' : node.name}
                      </p>
                      <p className="text-xs text-slate-500">{node.attempts} attempts</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-sm font-medium flex items-center gap-0.5 ${
                      node.growth >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {getGrowthIcon(node.growth)}
                      {node.growth >= 0 ? '+' : ''}{node.growth}%
                    </span>
                    <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${isSelected ? 'rotate-90' : ''}`} />
                  </div>
                </button>
              );
            })}
          </div>
        </div>

      </CardContent>
    </Card>
  );
}
